import Vue from 'vue';
import isAfter from 'date-fns/isAfter';
import { delay, debounce } from 'lodash';
import { apolloClient } from '@/plugins/apollo';
import { v4 as createUUID } from 'uuid';
import { UploadReferences, DeleteUploadGroup } from '@/graphql';
import { ReferenceFile } from '@/models';
import { FileUploadKind, FileSource, UploadState } from '@/enums';

const areUploadsInProgress = (context) => {
  const uploadStates = context.state.modal.filesToUpload
    .map(({ id }) => context.rootState.upload.uploads[id])
    .filter(Boolean);
  return uploadStates.some(({ complete }) => !complete);
};

/**
 * Everything that changes when references are re-linked or removed: the per-file
 * upload breakdown, the review-wide duplicate counts, and every list derived from
 * them. Apollo skips names that aren't currently active, so listing queries
 * belonging to other pages is a cheap no-op.
 *
 * Shared with UploadGroupList's manual duplicate re-run, which moves the same
 * surfaces.
 */
export const REFETCH_AFTER_DUPLICATE_CHANGE = [
  'GetReviewForReviewPage',
  'GetReviewForUploadsPage',
  'GetReviewUploads',
  'GetReviewForScreeningPage',
  'GetReviewScreeningInsights',
  'GetReferencesByFilters',
  'GetReferencesByReviewIdAndFilters',
  'SearchReferences',
  'GetRelevanceAssessmentCountsByReviewIdAndFilters',
  'GetDuplicateResolutionPage',
  'GetReferenceDuplicateGroups',
  'GetReferenceDuplicateGroupsAutoResolved',
  'GetReferenceDuplicateGroupsResolvedByUser',
  'GetReferenceDuplicateGroupsResolvedAsNotDuplicates',
];

const refetchReferenceList = debounce(() => {
  apolloClient.refetchQueries({
    include: REFETCH_AFTER_DUPLICATE_CHANGE,
  });
}, 500);

const state = {
  modal: {
    show: false,
    // Array<{ id: string; file: File }>
    filesToUpload: [],
    // Array<string>
    watchUploadGroups: [],
  },
  progressToast: {
    dismissed: true,
    // Array<string>
    watchFileUploads: [],
    // Array<string>
    watchUploadGroups: [],
  },
  referenceFileMap: {},
};

const actions = {
  /** ================ MODAL ================ */
  openModal(context, { files, watchUploadGroups } = {}) {
    if (files) {
      context.commit('setModalFilesToUpload', files);
    }
    if (watchUploadGroups) {
      context.commit(
        'setWatchUploadGroupsInModal',
        watchUploadGroups
      );
    }
    context.commit('setShowModal', true);
  },
  closeModal(context) {
    context.commit('setShowModal', false);
    context.dispatch('clearModalStateIfUploadingComplete');
    context.dispatch('clearProgressToastStateIfProcessingComplete');
  },
  closeModalIfUploadingComplete(context) {
    if (!areUploadsInProgress(context)) {
      context.dispatch('closeModal');
    }
  },
  clearModalStateIfUploadingComplete(context) {
    if (!areUploadsInProgress(context)) {
      delay(() => context.dispatch('clearModalState'), 500);
    }
  },
  setModalFilesToUpload(context, files) {
    context.commit('setModalFilesToUpload', files);
  },
  async beginUpload(
    context,
    { reviewId, fileIdToPreScreenedOutcome, fileIdToSource }
  ) {
    // Create new upload group ID for this upload and start watching it in the modal.
    const uploadGroupId = createUUID();
    context.commit('setWatchUploadGroupsInModal', [uploadGroupId]);

    // Notify the progress toast that it should start watching this upload group.
    context.commit('watchUploadGroupInProgressToast', uploadGroupId);

    for (const fileToUpload of context.state.modal.filesToUpload) {
      const preScreenedOutcome =
        fileIdToPreScreenedOutcome[fileToUpload.id];
      const source =
        fileIdToSource[fileToUpload.id] || FileSource.OTHER;
      context.dispatch('uploadReferenceFile', {
        fileToUpload,
        uploadGroupId,
        reviewId,
        preScreenedOutcome,
        source,
      });
    }
  },
  async cancelUpload(context, upload) {
    await context.dispatch('upload/cancelSingleUpload', upload, {
      root: true,
    });
    context.commit('stopWatchingFileUploadInModal', upload);
    context.commit('stopWatchingFileUploadInProgressToast', upload);
  },
  async uploadReferenceFile(
    context,
    {
      fileToUpload,
      uploadGroupId,
      reviewId,
      preScreenedOutcome,
      source,
    }
  ) {
    const { id, file } = fileToUpload;

    // Notify the progress toast to watch this file upload.
    context.commit('watchFileUploadInProgressToast', id);

    // Determine the kind & content type of the file upload.
    let kind;
    let contentType;
    if (file.name.toLowerCase().endsWith('.ris')) {
      kind = FileUploadKind.REFERENCE_RIS;
      contentType = 'application/x-research-info-systems';
    } else if (file.name.toLowerCase().endsWith('.xml')) {
      kind = FileUploadKind.REFERENCE_END_NOTE;
      contentType = 'application/xml';
    } else {
      context.commit(
        'stopWatchingFileUploadInProgressToast',
        fileToUpload
      );
      throw { message: 'Unknown file type provided during upload' };
    }

    // Upload the file to object storage.
    const fileUri = await context.dispatch(
      'upload/uploadFile',
      {
        id,
        file,
        reviewId,
        kind,
        contentType,
      },
      { root: true }
    );

    if (id in context.rootState.upload.errors) {
      context.commit(
        'stopWatchingFileUploadInProgressToast',
        fileToUpload
      );
      return;
    }

    let stillWatchingUpload = !!context.state.modal.filesToUpload.find(
      (upload) => upload.id === id
    );
    if (stillWatchingUpload) {
      // Allow the user to upload more files if all uploads are complete.
      context.state.modal.show
        ? await context.dispatch('closeModalIfUploadingComplete')
        : await context.dispatch(
            'clearModalStateIfUploadingComplete'
          );
    }

    try {
      // Store an optimistic response for the reference file.
      // Prevents the modal being empty when the user re-opens it while the GraphQL mutation is in flight.
      const optimisticResponse = ReferenceFile.optimisticResponse(
        fileUri,
        uploadGroupId
      );
      context.commit('storeReferenceFile', optimisticResponse);

      // Start the processing of the uploaded file.
      const response = await apolloClient.mutate({
        mutation: UploadReferences,
        variables: {
          fileUri,
          uploadGroupId,
          reviewId,
          source,
          preScreenedOutcome,
        },
        refetchQueries: [
          'GetReviewForReviewPage',
          'GetReviewForUploadsPage',
          'GetReviewUploads',
          'GetReviewForScreeningPage',
          'GetReviewScreeningInsights',
          'GetReferencesByReviewIdAndFilters',
          'SearchReferences',
          'GetSampleReferences',
          'GetRelevanceAssessmentCountsByReviewIdAndFilters',
        ],
      });
      context.commit('clearReferenceFile', optimisticResponse.key);

      const referenceFile = new ReferenceFile(
        response.data.uploadReferences
      );
      context.commit('storeReferenceFile', referenceFile);

      return referenceFile;
    } catch (error) {
      context.commit(
        'stopWatchingFileUploadInProgressToast',
        fileToUpload
      );
      throw error;
    }
  },
  clearModalState(context) {
    if (context.state.modal.show) return;
    context.commit('setModalFilesToUpload', []);
    context.commit('setWatchUploadGroupsInModal', []);
  },
  /** ================ PROGRESS TOAST ================ */
  viewProgress(context) {
    const watchUploadGroups = state.progressToast.watchUploadGroups;
    context.dispatch('openModal', {
      watchUploadGroups,
    });
  },
  dismissProgressToast(context, { withDelay = true } = {}) {
    context.commit('setDismissedProgressToast', true);
    if (withDelay) {
      delay(() => context.dispatch('clearProgressToastState'), 500);
    } else {
      context.dispatch('clearProgressToastState');
    }
  },
  clearProgressToastStateIfProcessingComplete(context) {
    // Build the set of URIs currently being watched by the toast
    const uploadStates = context.state.progressToast.watchFileUploads
      .map((id) => context.rootState.upload.uploads[id])
      .filter(Boolean);
    const watchedUris = uploadStates
      .map((u) => u.uri)
      .filter(Boolean);

    // Fetch all ReferenceFile records that the progress toast is watching
    // Either by upload group or by matching URI from a watched upload
    const watchingFilesProcessing = Object.values(
      context.state.referenceFileMap
    ).filter(
      (referenceFile) =>
        state.progressToast.watchUploadGroups.includes(
          referenceFile.uploadGroupId
        ) || watchedUris.includes(referenceFile.uri)
    );

    // Fetch all uploads that the progress toast is watching.
    // (reuse uploadStates from above)

    // Uploads are considered "in progress" if they don't have a ReferenceFile record.
    const uploadsInProgress = uploadStates.some(
      ({ uri, complete }) =>
        !complete ||
        !watchingFilesProcessing.find(
          (referenceFile) => referenceFile.uri === uri
        )
    );

    const allProcessingComplete = watchingFilesProcessing.every(
      (referenceFile) => referenceFile.isDone()
    );
    if (!uploadsInProgress && allProcessingComplete) {
      context.dispatch('dismissProgressToast', { withDelay: false });
    }
  },
  clearProgressToastState(context) {
    context.commit('setWatchFileUploadsInProgressToast', []);
    context.commit('setWatchUploadGroupsInProgressToast', []);
    delay(() => context.commit('clearReferenceFileCache'), 500);
  },
  /** ================ SUBSCRIPTION EVENTS ================ */
  async onReferenceFileCreate(context, referenceFile) {
    await context.dispatch(
      'refetchOnNewReferencesAdded',
      referenceFile
    );
    context.commit('storeReferenceFile', referenceFile);
    context.commit(
      'watchUploadGroupInProgressToast',
      referenceFile.uploadGroupId
    );
  },
  async onReferenceFileUpdate(context, referenceFile) {
    await context.dispatch(
      'refetchOnNewReferencesAdded',
      referenceFile
    );
    context.commit('storeReferenceFile', referenceFile);
  },
  refetchOnNewReferencesAdded(context, referenceFile) {
    const upload = Object.values(
      context.rootState.upload.uploads
    ).find((u) => u.uri === referenceFile.uri);
    if (!upload) return;

    const isWatching = context.state.progressToast.watchFileUploads.some(
      (id) => id === upload.id
    );
    const existing = context.state.referenceFileMap[referenceFile.id];
    const prevReferenceCount = existing?.newReferenceCount || 0;
    const newReferences =
      referenceFile.newReferenceCount - prevReferenceCount;

    // If new references were added, refresh the reference list.
    if (isWatching && newReferences > 0) {
      refetchReferenceList();
    }
  },
  async deleteUploadGroup(context, uploadGroupId) {
    // Deleting a group removes references and re-runs duplicate detection on the
    // server, so it moves everything a manual re-run moves — including the
    // duplicate-group lists. Awaited so the caller's snackbar is not shown before
    // the lists the user is looking at have caught up.
    await apolloClient.mutate({
      mutation: DeleteUploadGroup,
      variables: { uploadGroupId },
      refetchQueries: REFETCH_AFTER_DUPLICATE_CHANGE,
      awaitRefetchQueries: true,
    });
  },
};

const mutations = {
  /** ================ MODAL ================ */
  setShowModal(state, show) {
    Vue.set(state.modal, 'show', show);
  },
  setModalFilesToUpload(state, files) {
    Vue.set(state.modal, 'filesToUpload', files);
  },
  setWatchUploadGroupsInModal(state, watchUploadGroups) {
    Vue.set(state.modal, 'watchUploadGroups', watchUploadGroups);
  },
  stopWatchingFileUploadInModal(state, upload) {
    state.modal.filesToUpload = state.modal.filesToUpload.filter(
      (fileToUpload) => fileToUpload.id !== upload.id
    );
  },
  /** ================ PROGRESS TOAST ================ */
  setWatchFileUploadsInProgressToast(state, watchFileUploads) {
    Vue.set(
      state.progressToast,
      'watchFileUploads',
      watchFileUploads
    );
  },
  stopWatchingFileUploadInProgressToast(state, upload) {
    state.progressToast.watchFileUploads = state.progressToast.watchFileUploads.filter(
      (id) => id !== upload.id
    );
  },
  setWatchUploadGroupsInProgressToast(state, watchUploadGroups) {
    Vue.set(
      state.progressToast,
      'watchUploadGroups',
      watchUploadGroups
    );
  },
  watchUploadGroupInProgressToast(state, id) {
    state.progressToast.watchUploadGroups.push(id);
    Vue.set(state.progressToast, 'dismissed', false);
  },
  watchFileUploadInProgressToast(state, id) {
    state.progressToast.watchFileUploads.push(id);
  },
  setDismissedProgressToast(state, dismissed) {
    Vue.set(state.progressToast, 'dismissed', dismissed);
  },
  /** ================ SUBSCRIPTION EVENTS ================ */
  clearReferenceFileCache(state) {
    const anyInProgress = Object.values(state.referenceFileMap).some(
      (referenceFile) => !referenceFile.isDone()
    );
    if (!anyInProgress) {
      state.referenceFileMap = {};
    }
  },
  storeReferenceFile(state, referenceFile) {
    // Remove any optimistic version of the file.
    const optimisticValue = Object.values(
      state.referenceFileMap
    ).find(
      (existing) =>
        existing.uri === referenceFile.uri && existing.optimisticId
    );
    if (optimisticValue) {
      Vue.delete(state.referenceFileMap, optimisticValue.key);
    }

    // Ignore old updates.
    const existing = state.referenceFileMap[referenceFile.key];
    const isOld =
      existing &&
      isAfter(existing.updatedAt, referenceFile.updatedAt);
    if (isOld) return;

    // Sometimes time isn't high enough resolution to detect a change.
    // If the new update is in an earlier state, ignore it.
    if (existing) {
      const states = Object.values(UploadState);
      const existingState = states.indexOf(existing.uploadState);
      const newState = states.indexOf(referenceFile.uploadState);

      if (newState < existingState) {
        return;
      }
    }

    // Show the progress toast if the file is processing.
    const toastNotWatching = !state.progressToast.watchUploadGroups.includes(
      referenceFile.uploadGroupId
    );
    if (!referenceFile.isDone() && toastNotWatching) {
      state.progressToast.watchUploadGroups.push(
        referenceFile.uploadGroupId
      );
      Vue.set(state.progressToast, 'dismissed', false);
    }

    // Store the reference file.
    // TODO: Find a way to use the apollo cache instead.
    Vue.set(
      state.referenceFileMap,
      referenceFile.key,
      new ReferenceFile(referenceFile)
    );
  },
  clearReferenceFile(state, referenceFileId) {
    Vue.delete(state.referenceFileMap, referenceFileId);
  },
};

const getters = {
  /** ================ MODAL ================ */
  showModal: (state) => state.modal.show,
  awaitingUpload: (state, getters, rootState) => {
    return state.modal.filesToUpload.every(
      (file) => !(file.id in rootState.upload.uploads)
    );
  },
  areModalUploadsInProgress: (state, getters, rootState) => {
    // Uploads are considered in progress if they have been submitted
    // but we don't yet have a ReferenceFile for their URI (from any group).
    const submittedUploads = rootState.upload.uploads;
    const allReferenceFiles = Object.values(state.referenceFileMap);
    return state.modal.filesToUpload.some((upload) => {
      const submittedUpload = submittedUploads[upload.id];
      if (!submittedUpload) return false; // not started
      const haveReferenceFile = allReferenceFiles.find(
        (referenceFile) => referenceFile.uri === submittedUpload.uri
      );
      return !haveReferenceFile;
    });
  },
  modalWatchingReferenceFiles: (state, getters, rootState) => {
    // Include files in the current modal's upload groups OR
    // any ReferenceFile whose URI matches a file the modal submitted.
    const submittedUris = state.modal.filesToUpload
      .map(({ id }) => rootState.upload.uploads[id])
      .filter(Boolean)
      .map((u) => u.uri);
    return Object.values(state.referenceFileMap).filter(
      (referenceFile) =>
        state.modal.watchUploadGroups.includes(
          referenceFile.uploadGroupId
        ) || submittedUris.includes(referenceFile.uri)
    );
  },
  areModalUploadsStillProcessing: (state, getters) => {
    return (
      getters.modalWatchingReferenceFiles.length > 0 &&
      getters.modalWatchingReferenceFiles.some(
        (referenceFile) => !referenceFile.isDone()
      )
    );
  },
  modalFilesToUpload: (state, getters, rootState, rootGetters) =>
    state.modal.filesToUpload.map((upload) => ({
      ...upload,
      ...rootGetters['upload/uploads'][upload.id],
    })),
  /** ================ PROGRESS TOAST ================ */
  showProgressToast: (state) =>
    !state.progressToast.dismissed &&
    !state.modal.show &&
    state.progressToast.watchUploadGroups.length > 0,
  progressToastWatchingReferenceFiles: (
    state,
    getters,
    rootState
  ) => {
    const refs = Object.values(state.referenceFileMap);
    const watchedUris = state.progressToast.watchFileUploads
      .map((id) => rootState.upload.uploads[id])
      .filter(Boolean)
      .map((u) => u.uri);
    return refs.filter(
      (referenceFile) =>
        state.progressToast.watchUploadGroups.includes(
          referenceFile.uploadGroupId
        ) || watchedUris.includes(referenceFile.uri)
    );
  },
  progressToastUploadingCount: (state, getters, rootState) => {
    const uploadStates = state.progressToast.watchFileUploads.map(
      (id) => rootState.upload.uploads[id]
    );
    return uploadStates
      .filter(Boolean)
      .filter(
        ({ complete, uri }) =>
          !complete ||
          !getters.progressToastWatchingReferenceFiles.find(
            (referenceFile) => referenceFile.uri === uri
          )
      ).length;
  },
  progressToastProcessingCount: (state, getters) => {
    return getters.progressToastWatchingReferenceFiles.filter(
      (referenceFile) => !referenceFile.isDone()
    ).length;
  },
  progressToastCompleteCount: (state, getters) => {
    return getters.progressToastWatchingReferenceFiles.filter(
      (referenceFile) => referenceFile.isDone()
    ).length;
  },
  progressToastWatchingUploadGroups: (state) =>
    state.progressToast.watchUploadGroups,
  /** ================ MISC ================ */
  uploadsAreInProgress: (state, getters) =>
    getters.progressToastUploadingCount > 0 ||
    getters.progressToastProcessingCount > 0,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
